import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { ImagePlus, Image, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAtmosphere } from './Atmosphere';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AtmosphereUploader() {
  const portraitRef = useRef(null);
  const wallpaperRef = useRef(null);
  const { backgroundImage, wallpaperImage, setAtmosphere, refresh } = useAtmosphere();
  const [busy, setBusy] = useState(null);

  const upload = async (file, kind) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image must be under 8MB');
      return;
    }

    setBusy(kind);
    try {
      const form = new FormData();
      form.append('file', file);
      const endpoint =
        kind === 'wallpaper'
          ? '/api/atmosphere/wallpaper'
          : '/api/atmosphere/background';
      const { data } = await axios.post(`${API_URL}${endpoint}`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAtmosphere({
        backgroundImage: data.background_image,
        wallpaperImage: data.wallpaper_image,
        brideName: data.bride_name,
        groomName: data.groom_name,
      });
      toast.success(
        kind === 'wallpaper'
          ? 'Background wallpaper updated'
          : 'Portrait photo updated'
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(null);
    }
  };

  const clear = async (kind) => {
    setBusy(kind);
    try {
      const endpoint =
        kind === 'wallpaper'
          ? '/api/atmosphere/wallpaper'
          : '/api/atmosphere/background';
      await axios.delete(`${API_URL}${endpoint}`, { withCredentials: true });
      await refresh();
      toast.success(kind === 'wallpaper' ? 'Wallpaper reset' : 'Portrait removed');
    } catch {
      toast.error('Could not clear image');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col items-stretch sm:items-end gap-2" data-testid="atmosphere-uploader">
      <input
        ref={portraitRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          upload(f, 'portrait');
        }}
      />
      <input
        ref={wallpaperRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          upload(f, 'wallpaper');
        }}
      />

      <div className="flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          className="rounded-full"
          disabled={!!busy}
          onClick={() => portraitRef.current?.click()}
          data-testid="upload-portrait-button"
        >
          {busy === 'portrait' ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <ImagePlus className="h-4 w-4 mr-2" />
          )}
          {backgroundImage ? 'Change portrait' : 'Upload portrait'}
        </Button>
        {backgroundImage && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={!!busy}
            onClick={() => clear('portrait')}
            title="Remove portrait"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          className="rounded-full bg-background/80"
          disabled={!!busy}
          onClick={() => wallpaperRef.current?.click()}
          data-testid="upload-wallpaper-button"
        >
          {busy === 'wallpaper' ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Image className="h-4 w-4 mr-2" />
          )}
          {wallpaperImage ? 'Change wallpaper' : 'Set background wallpaper'}
        </Button>
        {wallpaperImage && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={!!busy}
            onClick={() => clear('wallpaper')}
            title="Reset to default wallpaper"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground max-w-[220px] text-right leading-snug">
        Tip: use a wide landscape photo for wallpaper; keep the couple shot as portrait.
      </p>
    </div>
  );
}
